// Global Instructions Rule Applied!
//
// Fund full-form Statements tab (spec A3.1/A3.3): renders the cfg-driven
// financial statements - Income Statement and Balance Sheet from the
// cfg.acc_statement_* layout, the Statement of Partners' Capital (per-LP
// roll-forward + independently-computed fund total) and the single-LP
// Capital Account statement. Selectors: statement type (from cfg), financial
// year, optional as-of cap, and an LP picker for the LP capital account.
//
// The layout decides what the Income Statement and Balance Sheet print
// (spec A3.14 stage 2): groups (a header, its lines one indent deeper and a
// subtotal - or one figure for the whole group), lines left out when they
// are zero, and one amount column per statement column (Quarter and Year to
// date on the income statement). The rows arrive ready to print; this tab
// only indents them and lays the columns out.
//
// The LP Capital Account is laid out on the same layout (spec A3.14 stage
// 3): the roll-forward lines the printed Capital Account Statement uses, in
// three columns - Quarter, Year to date, Since inception - which all close on
// the same balance. It renders through the statement table, like the Income
// Statement.
//
// Sign conventions: IS/BS rows arrive display-signed from the server (every
// line reads positive in its natural presentation); roll-forwards are
// credit-positive (contributions/profit positive, fees/distributions
// negative). The BS states the result of all earlier years apart from the
// year to date and carries a Balanced tag. Where investment accounts are
// configured it has a second column, fair market value (spec A3.14 stage 3):
// investments at the measures engine's fair market value and its unrealized
// gain in capital. That column balances exactly when the ledger's investments
// at cost agree with the investment records, so the read also says whether
// they do: a Reconciled / Not reconciled tag, with both figures on hover.
// Partners' Capital verifies sum-of-rows against the fund total computed
// straight from fund-level journals.
//
// SHARED COMPONENT (EqualizationSection precedent): the optional `group`
// prop switches the tab into CONSOLIDATED mode for the fund-group form -
// same statement types, same row shapes. The Income Statement and Balance
// Sheet are computed across all member funds with the intercompany
// pass-through eliminated. The roll-forwards run per CONSOLIDATED COMMITMENT
// (the LP selector lists them): an investor's consolidated capital account
// is the sum of its accounts in the legal entities (spec A3.14, owner
// decision 7). The FY selector lists the PRIMARY fund's years. Without
// `group` the fund path is unchanged.
//
// Under the statements sits the Investor Statements section (spec A3.14
// stage 3, part 3; InvestorStatementsSection.tsx): the checks for a quarter
// end and a live preview of the PDFs investors receive.
// Under it the Partner Allocation section (PartnerAllocationSection.tsx): the
// K-1 working paper per partner and tax category, with its workbook.
//
// Lightweight fetch-and-render recipe (LedgerTab precedent): tab-local
// state, no redux slice.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Descriptions, Row, Select, Table, Tag, Tooltip, Typography } from 'antd'
import DatePicker from '../../../../../core/components/DatePicker.tsx'
import { DownloadOutlined, FileExcelOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../../../core/services/utilitiesService.ts'
import {
  fetchFundStatementOptions,
  fetchFundStatement,
  fetchFundPartnersCapital,
  fetchFundLpCapitalAccount,
  fetchFundFinYears,
  downloadFundReviewWorkbook
} from '../../../services/fundsService.ts'
import {
  fetchFundGroupStatementOptions,
  fetchFundGroupStatement,
  fetchFundGroupPartnersCapital,
  fetchFundGroupLpCapitalAccount
} from '../../../../fund_groups/services/fundGroupsService.ts'
import { flattenStatementRows } from '../../../utils/statementExport.ts'
import InvestorStatementsSection from './InvestorStatementsSection.tsx'
import PartnerAllocationSection from './PartnerAllocationSection.tsx'
import StatementSettingsSection from './StatementSettingsSection.tsx'
import FundStatementRunsSection from './FundStatementRunsSection.tsx'
import WaterfallPositionSection from './WaterfallPositionSection.tsx'
import type {
  FundStatementRow,
  FundStatementMeta,
  FundStatementColumn,
  FundPartnersCapitalRow,
  FundPartnersCapitalTotal,
  FundLpCapitalAccountHeader,
  FundStatementTypeOption,
  FundStatementLpOption
} from '../../../../../state_manager/data_models/fund_statements.ts'
import type { FundLedgerFinYear } from '../../../../../state_manager/data_models/fund_ledger.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const disp = (v: unknown): string => (v != null && v !== '' ? String(v) : '-')
const pct = (v: number): string => `${(v * 100).toFixed(2)}%`

const SECTIONED_CODES = ['income_statement', 'balance_sheet']

// the label column of the consolidated fund's statement settings (the Terms
// tab's own width)
const SETTINGS_LABEL_WIDTH = 235

interface StatementsTabProps {
  fundId: string | null
  /** Consolidated mode (fund-group form): statements across member funds
   *  with intercompany eliminated; LP selector = subscriptions. */
  group?: { fundGroupId: string; primaryFundId: string | null }
}

export default function StatementsTab({ fundId, group }: StatementsTabProps) {
  const isGroup = Boolean(group)
  // cfg.waived_fee_settlements: the Outstanding figure is explained by the
  // settlement's own cfg description (spec A3.9 addendum part 1)
  const waivedFeeSettlements = useSelector((state: any) => state.globalConfigs?.waived_fee_settlements ?? [])
  const [typeOptions, setTypeOptions] = useState<FundStatementTypeOption[]>([])
  const [lpOptions, setLpOptions] = useState<FundStatementLpOption[]>([])
  const [fyOptions, setFyOptions] = useState<Array<{ value: string; label: string }>>([])

  const [statementCode, setStatementCode] = useState<string>('income_statement')
  const [selectedFy, setSelectedFy] = useState<string | null>(null)
  const [asOf, setAsOf] = useState<string | null>(null)
  const [selectedLp, setSelectedLp] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [finYear, setFinYear] = useState<FundLedgerFinYear | null>(null)
  const [stmtRows, setStmtRows] = useState<FundStatementRow[]>([])
  const [stmtMeta, setStmtMeta] = useState<FundStatementMeta | null>(null)
  const [stmtColumns, setStmtColumns] = useState<FundStatementColumn[]>([])
  const [stmtTotals, setStmtTotals] = useState<Record<string, unknown>>({})
  const [pcRows, setPcRows] = useState<FundPartnersCapitalRow[]>([])
  const [pcTotal, setPcTotal] = useState<FundPartnersCapitalTotal | null>(null)
  const [lpRows, setLpRows] = useState<FundStatementRow[]>([])
  const [lpStmtColumns, setLpStmtColumns] = useState<FundStatementColumn[]>([])
  const [lpHeader, setLpHeader] = useState<FundLpCapitalAccountHeader | null>(null)
  const [allocationNote, setAllocationNote] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  const load = useCallback(
    async (code: string, fyId: string | null, asOfDate: string | null, lpId: string | null) => {
      if (!fundId && !group) return
      setLoading(true)
      try {
        const token = getAccessToken(store.getState().auth)
        setStatusMessage(null)
        if (SECTIONED_CODES.includes(code)) {
          const res = group
            ? await fetchFundGroupStatement(group.fundGroupId, { code, fyId, asOf: asOfDate }, token)
            : await fetchFundStatement(fundId as string, { code, fyId, asOf: asOfDate }, token)
          if (res.error) showErrorToast(res.error)
          setStmtRows(res.data)
          setStmtMeta(res.statement)
          setStmtColumns(res.columns)
          setStmtTotals(res.totals)
          setFinYear(res.finYear)
          setStatusMessage(res.statusMessage ?? null)
          if (!fyId && res.finYear?.id) setSelectedFy(res.finYear.id)
        } else if (code === 'partners_capital') {
          const res = group
            ? await fetchFundGroupPartnersCapital(group.fundGroupId, { fyId, asOf: asOfDate }, token)
            : await fetchFundPartnersCapital(fundId as string, { fyId, asOf: asOfDate }, token)
          if (res.error) showErrorToast(res.error)
          setPcRows(res.data)
          setPcTotal(res.fundTotal)
          setFinYear(res.finYear)
          setAllocationNote(res.allocationNote ?? null)
          if (!fyId && res.finYear?.id) setSelectedFy(res.finYear.id)
        } else if (code === 'lp_capital_account') {
          if (!lpId) {
            setLpRows([])
            setLpStmtColumns([])
            setLpHeader(null)
            return
          }
          // "<commitment>|<transfer>" selects a prior holder's tenure (a
          // transferred commitment lists its earlier holders too)
          const [commitmentId, transferId] = lpId.split('|')
          const res = group
            ? await fetchFundGroupLpCapitalAccount(group.fundGroupId, commitmentId, { fyId, asOf: asOfDate }, token)
            : await fetchFundLpCapitalAccount(
                fundId as string,
                commitmentId,
                { fyId, asOf: asOfDate, fundCommitmentTransferId: transferId ?? null },
                token
              )
          if (res.error) showErrorToast(res.error)
          setLpRows(res.data)
          setLpStmtColumns(res.columns)
          setLpHeader(res.header)
          setFinYear(res.finYear)
          setAllocationNote(res.allocationNote ?? null)
          setStatusMessage(res.statusMessage ?? null)
          if (!fyId && res.finYear?.id) setSelectedFy(res.finYear.id)
        }
      } finally {
        setLoading(false)
      }
    },
    [fundId, group]
  )

  useEffect(() => {
    if (!fundId && !group) return
    const token = getAccessToken(store.getState().auth)
    // FY options come from the primary fund in group mode (owner decision:
    // the primary's FY calendar drives the consolidated window)
    const optionsPromise = group
      ? fetchFundGroupStatementOptions(group.fundGroupId, token)
      : fetchFundStatementOptions(fundId as string, token)
    const fyPromise = group
      ? group.primaryFundId
        ? fetchFundFinYears(group.primaryFundId, token)
        : Promise.resolve({ data: [] as Array<{ value: string; label: string }> })
      : fetchFundFinYears(fundId as string, token)
    void Promise.all([optionsPromise, fyPromise]).then(([opts, fy]) => {
      if (opts.error) showErrorToast(opts.error)
      setTypeOptions(opts.statementTypes)
      setLpOptions(opts.lps)
      setFyOptions(fy.data)
    })
    void load('income_statement', null, null, null)
  }, [fundId, group, load])

  const refresh = useCallback(() => {
    void load(statementCode, selectedFy, asOf, selectedLp)
  }, [load, statementCode, selectedFy, asOf, selectedLp])

  const currentLabel = useMemo(
    () =>
      typeOptions.find((t) => t.cfg_acc_statement_types__code === statementCode)
        ?.cfg_acc_statement_types__label ?? 'Statement',
    [typeOptions, statementCode]
  )

  const handleExport = useCallback(async () => {
    const token = getAccessToken(store.getState().auth)
    let data: Record<string, unknown>[] = []
    let columns: string[] = []
    if (SECTIONED_CODES.includes(statementCode)) {
      ;({ data, columns } = flattenStatementRows(stmtRows, stmtColumns))
    } else if (statementCode === 'partners_capital') {
      data = pcRows.map((r) => ({
        Investor: r.investors__name,
        'Ownership %': r.prog__ownership_pct,
        Commitment: r.fund_commitments__amount ?? 0,
        Opening: r.prog__opening,
        Contributions: r.prog__contributions,
        'Net income allocation': r.prog__net_income_allocation,
        Distributions: r.prog__distributions,
        Transfers: r.prog__transfers ?? 0,
        Ending: r.prog__ending
      }))
      columns = ['Investor', 'Ownership %', 'Commitment', 'Opening', 'Contributions', 'Net income allocation', 'Distributions', 'Transfers', 'Ending']
    } else {
      ;({ data, columns } = flattenStatementRows(lpRows, lpStmtColumns))
    }
    if (data.length === 0) {
      toast.info('Nothing to export')
      return
    }
    const res = await exportJsonToExcel(
      {
        data,
        columns,
        filename: `${statementCode}_${finYear?.fin_year ?? ''}`,
        ...(statementCode === 'partners_capital' ? { percentageColumns: ['Ownership %'] } : {})
      },
      token
    )
    if (!res.success) showErrorToast(res.error || 'Export failed')
  }, [statementCode, stmtRows, stmtColumns, pcRows, lpRows, lpStmtColumns, finYear])

  // the Review workbook: the fund's quarter as one working paper, as of the
  // selected date (else the window end of the financial year shown). A
  // consolidated fund has none: its legal entities each have one.
  const [downloadingWorkbook, setDownloadingWorkbook] = useState(false)
  const reviewAsOf = asOf ?? finYear?.window_end ?? finYear?.year_end_date ?? null
  const handleReviewWorkbook = useCallback(async () => {
    if (!fundId || !reviewAsOf) return
    setDownloadingWorkbook(true)
    try {
      const res = await downloadFundReviewWorkbook(fundId, reviewAsOf, getAccessToken(store.getState().auth))
      if (!res.success) showErrorToast(res.error || 'Failed to download the review workbook')
    } finally {
      setDownloadingWorkbook(false)
    }
  }, [fundId, reviewAsOf])

  // ---- renderers -----------------------------------------------------------

  // the columns of a statement laid out on the layout: the line, then one
  // amount per statement column. Shared by the Income Statement, the Balance
  // Sheet and the LP Capital Account. datedToEnd: on a roll-forward, a column
  // with an end and no start runs from inception - say so under its heading.
  const statementTableColumns = (columns: FundStatementColumn[], datedToEnd = false) => [
    {
      title: currentLabel,
      dataIndex: 'prog__label',
      render: (_: unknown, row: FundStatementRow) => {
        if (row.prog__row_kind === 'header') {
          // a section header sits at the margin; the header of a group
          // printed with its lines sits on its lines' indent
          return (
            <Text strong className='text-gray-600' style={{ paddingLeft: row.prog__indent * 16 }}>
              {row.prog__label}
            </Text>
          )
        }
        const label = row.acc_gl_accounts__account_code
          ? `${row.acc_gl_accounts__account_code} · ${row.prog__label}`
          : row.prog__label
        return (
          <span
            className={row.prog__row_kind === 'subtotal' || row.prog__row_kind === 'total' ? 'font-semibold' : ''}
            style={{ paddingLeft: row.prog__indent * 16 }}
          >
            {label}
          </span>
        )
      }
    },
    // one amount column per statement column; a statement with no column
    // configured carries its single amount in prog__amount_fmt
    ...(columns.length > 0 ? columns : [{ code: '', label: 'Amount' } as FundStatementColumn]).map((col) => ({
      // a period column says which dates it covers: without an as-of date
      // the quarter is the one the year END falls in, which is easy to
      // misread as "the latest quarter"
      title:
        col.window_end && (col.window_start || datedToEnd) ? (
          <div>
            <div>{col.label}</div>
            <Text type='secondary' className='text-xs font-normal'>
              {col.window_start
                ? `${dayjs(col.window_start).format(DATE_FORMAT)} – ${dayjs(col.window_end).format(DATE_FORMAT)}`
                : `to ${dayjs(col.window_end).format(DATE_FORMAT)}`}
            </Text>
          </div>
        ) : (
          col.label
        ),
      key: col.code || 'amount',
      width: 170,
      align: 'right' as const,
      render: (_: unknown, row: FundStatementRow) =>
        row.prog__row_kind === 'header' ? (
          ''
        ) : (
          <span className={row.prog__row_kind === 'subtotal' || row.prog__row_kind === 'total' ? 'font-semibold' : ''}>
            {disp(col.code ? row.prog__amounts_fmt?.[col.code] : row.prog__amount_fmt)}
          </span>
        )
    }))
  ]

  const pcColumns = [
    { title: 'Investor', dataIndex: 'investors__name', render: disp },
    {
      title: 'Ownership',
      dataIndex: 'prog__ownership_pct',
      width: 100,
      align: 'right' as const,
      render: (v: number) => pct(v ?? 0)
    },
    { title: 'Commitment', dataIndex: 'fund_commitments__amount_fmt', width: 130, align: 'right' as const, render: disp },
    { title: 'Opening', dataIndex: 'prog__opening_fmt', width: 120, align: 'right' as const, render: disp },
    { title: 'Contributions', dataIndex: 'prog__contributions_fmt', width: 130, align: 'right' as const, render: disp },
    { title: 'Net income alloc.', dataIndex: 'prog__net_income_allocation_fmt', width: 140, align: 'right' as const, render: disp },
    { title: 'Distributions', dataIndex: 'prog__distributions_fmt', width: 130, align: 'right' as const, render: disp },
    // transfers of interest: transfer out (-) / in (+) at the transfer date
    { title: 'Transfers', dataIndex: 'prog__transfers_fmt', width: 120, align: 'right' as const, render: disp },
    { title: 'Ending', dataIndex: 'prog__ending_fmt', width: 130, align: 'right' as const, render: disp },
    { title: 'Fee capital bal.', dataIndex: 'prog__fee_capital_balance_fmt', width: 130, align: 'right' as const, render: disp }
  ]

  // sum-of-rows vs fund-total consistency (partners capital)
  const pcMismatch = useMemo(() => {
    if (!pcTotal || pcRows.length === 0) return false
    const sum = pcRows.reduce((acc, r) => acc + (r.prog__ending ?? 0), 0)
    return Math.abs(sum - (pcTotal.prog__ending ?? 0)) >= 0.005
  }, [pcRows, pcTotal])

  if (!fundId && !group) {
    return <div className='p-6 text-gray-500'>Save the fund first to see its statements.</div>
  }

  const isBalanceSheet = statementCode === 'balance_sheet'
  const bsTotals = stmtTotals as {
    balanced?: boolean
    balanced_fmv?: boolean
    reconciled?: boolean
    investment_ledger_cost?: number
    investment_cost_held?: number
    reconciliation_difference?: number
  }
  const balanced = isBalanceSheet ? bsTotals.balanced : undefined
  // only a balance sheet with the fair market value column carries these
  const balancedFmv = isBalanceSheet ? bsTotals.balanced_fmv : undefined
  const reconciled = isBalanceSheet ? bsTotals.reconciled : undefined
  const money = (n?: number) =>
    (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const reconciliationTip =
    `Ledger, investments at cost: ${money(bsTotals.investment_ledger_cost)}. ` +
    `Investment records, cost held: ${money(bsTotals.investment_cost_held)}. ` +
    `Difference: ${money(bsTotals.reconciliation_difference)}.`

  return (
    <div className='h-full overflow-y-auto p-3'>
      <FormSection
        title='Financial Statements'
        titleColor='blue'
        loading={loading}
        headerActions={
          <div className='flex items-center gap-2'>
            <Select
              size='small'
              style={{ minWidth: 210 }}
              value={statementCode}
              options={typeOptions.map((t) => ({
                value: t.cfg_acc_statement_types__code,
                label: t.cfg_acc_statement_types__label
              }))}
              onChange={(v) => {
                setStatementCode(v)
                void load(v, selectedFy, asOf, selectedLp)
              }}
            />
            {statementCode === 'lp_capital_account' ? (
              <Select
                size='small'
                style={{ minWidth: 220 }}
                value={selectedLp}
                showSearch
                optionFilterProp='label'
                placeholder='Select LP'
                options={lpOptions.map((lp) => ({
                  // a prior holder of a transferred commitment keys on the
                  // transfer too (see load)
                  value: lp.fund_commitment_transfers__id
                    ? `${lp.fund_commitments__id}|${lp.fund_commitment_transfers__id}`
                    : lp.fund_commitments__id,
                  label: lp.investors__name
                }))}
                onChange={(v) => {
                  setSelectedLp(v)
                  void load(statementCode, selectedFy, asOf, v)
                }}
              />
            ) : null}
            <Select
              size='small'
              style={{ minWidth: 110 }}
              value={selectedFy}
              options={fyOptions}
              placeholder='Financial year'
              onChange={(v) => {
                setSelectedFy(v)
                void load(statementCode, v, asOf, selectedLp)
              }}
            />
            <DatePicker
              size='small'
              value={asOf ? dayjs(asOf) : null}
              format={DATE_FORMAT}
              placeholder='As of (FY end)'
              onChange={(d) => {
                const v = d ? d.format('YYYY-MM-DD') : null
                setAsOf(v)
                void load(statementCode, selectedFy, v, selectedLp)
              }}
            />
            <Button size='small' icon={<ReloadOutlined />} onClick={refresh}>
              Refresh
            </Button>
            <Button size='small' icon={<DownloadOutlined />} onClick={() => void handleExport()}>
              Export
            </Button>
            {!isGroup ? (
              <Tooltip title='One workbook for the reviewer: the statements, the schedule of investments, the partners capital, the trial balance and the journal lines since inception as linked tabs, as of the selected date'>
                <Button
                  size='small'
                  icon={<FileExcelOutlined />}
                  loading={downloadingWorkbook}
                  disabled={!reviewAsOf}
                  onClick={() => void handleReviewWorkbook()}
                >
                  Review workbook
                </Button>
              </Tooltip>
            ) : null}
          </div>
        }
      >
        {finYear ? (
          <div className='mb-2 flex items-center gap-3'>
            <Text type='secondary'>
              {currentLabel} — FY {finYear.fin_year}
              {finYear.year_start_date
                ? ` (${dayjs(finYear.year_start_date).format(DATE_FORMAT)} – ${dayjs(
                    finYear.window_end ?? finYear.year_end_date
                  ).format(DATE_FORMAT)})`
                : ''}
              {stmtMeta?.balance_basis === 'cumulative' && SECTIONED_CODES.includes(statementCode)
                ? ' — life-to-date'
                : ''}
              {finYear.closed ? ' — closed' : ''}
              {isGroup ? ' — Consolidated (intercompany transfers eliminated)' : ''}
            </Text>
            {isBalanceSheet && balanced !== undefined ? (
              balanced ? (
                <Tag color='green'>Balanced</Tag>
              ) : (
                <Tag color='red'>Out of balance</Tag>
              )
            ) : null}
            {isBalanceSheet && balancedFmv === false ? (
              <Tag color='red'>Out of balance at FMV</Tag>
            ) : null}
            {isBalanceSheet && reconciled !== undefined ? (
              <Tooltip title={reconciliationTip}>
                {reconciled ? <Tag color='green'>Reconciled</Tag> : <Tag color='gold'>Not reconciled</Tag>}
              </Tooltip>
            ) : null}
            {statementCode === 'partners_capital' && pcMismatch ? (
              <Tag color='red'>Rows ≠ fund total</Tag>
            ) : null}
          </div>
        ) : null}

        {statusMessage ? <Text type='warning'>{statusMessage}</Text> : null}

        {SECTIONED_CODES.includes(statementCode) ? (
          stmtRows.length === 0 && !loading ? (
            <Text type='secondary'>No statement rows — book transactions and refresh.</Text>
          ) : (
            <Table
              size='small'
              columns={statementTableColumns(stmtColumns)}
              dataSource={stmtRows.map((r, i) => ({ ...r, key: `${r.cfg_acc_statement_sections__code}-${i}` }))}
              pagination={false}
              rowKey='key'
              // a single amount column needs no heading; two or more do
              showHeader={stmtColumns.length > 1}
              style={{ maxWidth: 640 + Math.max(stmtColumns.length - 1, 0) * 170 }}
            />
          )
        ) : null}

        {statementCode === 'partners_capital' ? (
          pcRows.length === 0 && !loading ? (
            <Text type='secondary'>No commitments — the roll-forward appears once the fund has investors.</Text>
          ) : (
            <Table
              size='small'
              columns={pcColumns}
              dataSource={pcRows}
              pagination={false}
              // holder segments of a transferred commitment share its id
              rowKey={(r) => `${r.fund_commitments__id ?? ''}|${r.investors__name}`}
              summary={() =>
                pcTotal ? (
                  <Table.Summary fixed>
                    <Table.Summary.Row className='font-medium'>
                      <Table.Summary.Cell index={0}>Fund total</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} />
                      <Table.Summary.Cell index={2} />
                      <Table.Summary.Cell index={3} align='right'>
                        {disp(pcTotal.prog__opening_fmt)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align='right'>
                        {disp(pcTotal.prog__contributions_fmt)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align='right'>
                        {disp(pcTotal.prog__net_income_allocation_fmt)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align='right'>
                        {disp(pcTotal.prog__distributions_fmt)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align='right'>
                        {disp(pcTotal.prog__transfers_fmt)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align='right'>
                        {disp(pcTotal.prog__ending_fmt)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} />
                    </Table.Summary.Row>
                  </Table.Summary>
                ) : null
              }
            />
          )
        ) : null}

        {statementCode === 'lp_capital_account' ? (
          !selectedLp ? (
            <Text type='secondary'>Select an LP to render their capital account statement.</Text>
          ) : (
            <>
              {lpHeader ? (
                <Descriptions size='small' column={4} className='mb-2'>
                  <Descriptions.Item label='Investor'>
                    {lpHeader.investors__name}
                    {lpHeader.prog__holder_until ? (
                      <Text type='secondary' className='ml-2'>
                        (until {dayjs(lpHeader.prog__holder_until).format('MM/DD/YYYY')})
                      </Text>
                    ) : null}
                  </Descriptions.Item>
                  <Descriptions.Item label={isGroup ? 'Consolidated Commitment' : 'Commitment'}>
                    {disp(lpHeader.fund_commitments__amount_fmt)}
                  </Descriptions.Item>
                  <Descriptions.Item label='Ownership'>{pct(lpHeader.prog__ownership_pct ?? 0)}</Descriptions.Item>
                  <Descriptions.Item label='Status'>{disp(lpHeader.cfg_fund_commitment_statuses__label)}</Descriptions.Item>
                  <Descriptions.Item label='Fee capital balance'>
                    {disp(lpHeader.prog__fee_capital_balance_fmt)}
                  </Descriptions.Item>
                  {lpHeader.prog__waived_fees_to_date ? (
                    <Descriptions.Item label='Fees waived'>
                      <Tooltip
                        title={`Fees at the headline rate minus fees at this LP's rate, net of the fee cross-credit, through the last booked fee period${
                          lpHeader.prog__waived_fees_through
                            ? ` (${dayjs(lpHeader.prog__waived_fees_through).format('MM/DD/YYYY')})`
                            : ''
                        }.`}
                      >
                        <span>{disp(lpHeader.prog__waived_fees_to_date_fmt)}</span>
                      </Tooltip>
                    </Descriptions.Item>
                  ) : null}
                  {lpHeader.prog__waived_fees_repayable ? (
                    // what the fund still owes of the waived fees under its
                    // Waived Fee Settlement (owed less returned); labelled
                    // by the settlement. The consolidated statement adds the
                    // member funds' amounts and lists each one's settlement.
                    <Descriptions.Item
                      label={
                        lpHeader.prog__waived_fees_settlement
                          ? `Outstanding (${lpHeader.prog__waived_fees_settlement.toLowerCase()})`
                          : 'Outstanding'
                      }
                    >
                      <Tooltip
                        title={
                          <div>
                            <div>
                              Waived fees owed to this investor and not yet returned
                              {isGroup ? ', across the member funds.' : '.'}
                            </div>
                            {isGroup ? (
                              (lpHeader.prog__waived_fees_members ?? [])
                                .filter((m) => m.prog__waived_fees_repayable)
                                .map((m) => (
                                  <div key={m.funds__id ?? m.funds__name ?? ''}>
                                    {m.funds__name}: {disp(m.prog__waived_fees_repayable_fmt)}
                                    {m.prog__waived_fees_settlement
                                      ? ` (${m.prog__waived_fees_settlement.toLowerCase()})`
                                      : ''}
                                  </div>
                                ))
                            ) : (
                              <div>
                                {waivedFeeSettlements.find(
                                  (s: { label: string; description?: string | null }) =>
                                    s.label === lpHeader.prog__waived_fees_settlement
                                )?.description ?? ''}
                              </div>
                            )}
                          </div>
                        }
                      >
                        <span>{disp(lpHeader.prog__waived_fees_repayable_fmt)}</span>
                      </Tooltip>
                    </Descriptions.Item>
                  ) : null}
                </Descriptions>
              ) : null}
              {lpRows.length === 0 && !loading && !statusMessage ? (
                <Text type='secondary'>No capital account for this period.</Text>
              ) : (
                <Table
                  size='small'
                  columns={statementTableColumns(lpStmtColumns, true)}
                  dataSource={lpRows.map((r, i) => ({ ...r, key: `${r.cfg_acc_statement_sections__code}-${i}` }))}
                  pagination={false}
                  rowKey='key'
                  // a single amount column needs no heading; two or more do
                  showHeader={lpStmtColumns.length > 1}
                  style={{ maxWidth: 640 + Math.max(lpStmtColumns.length - 1, 0) * 170 }}
                />
              )}
            </>
          )
        ) : null}

        {allocationNote && statementCode !== 'income_statement' && statementCode !== 'balance_sheet' ? (
          <Text type='secondary' className='block mt-2'>
            {allocationNote}
          </Text>
        ) : null}
        {SECTIONED_CODES.includes(statementCode) ? (
          <Text type='secondary' className='block mt-2'>
            Layout, ordering, groups and subtotals come from the statement configuration; a line with
            nothing in any column is left out. The balance sheet states the result of all earlier
            years as accumulated net income / (loss), whether those years are closed or not, and the
            year to date on its own line. The fair market value column restates investments at fair
            market value and adds the unrealized gain to capital; it is never journaled, and it
            balances when the ledger&apos;s investments at cost agree with the investment records
            (Reconciled).
          </Text>
        ) : null}
      </FormSection>

      {/* the documents investors receive, previewed from the same books */}
      <InvestorStatementsSection fundId={fundId} group={group} lpOptions={lpOptions} />

      {/* the K-1 working paper: the year's allocation per partner and tax
          category (fund mode only) */}
      <PartnerAllocationSection fundId={fundId} group={group} fyOptions={fyOptions} />

      {/* where each partner stands in the waterfall, and the carried interest
          its statement states; a consolidated fund's over its own partners
          (waterfall phase 5) */}
      {group ? (
        <WaterfallPositionSection group={{ fundGroupId: group.fundGroupId }} />
      ) : fundId ? (
        <WaterfallPositionSection fundId={fundId} />
      ) : null}

      {/* the stored statements: the runs this fund is in */}
      <FundStatementRunsSection fundId={group ? null : fundId} fundGroupId={group?.fundGroupId ?? null} />

      {/* A consolidated fund has no Terms tab: its statement settings sit
          here (a fund's are on its Terms tab). */}
      {group ? (
        <Row gutter={[12, 12]} className='mt-3'>
          <StatementSettingsSection fundGroupId={group.fundGroupId} maxLabelWidth={SETTINGS_LABEL_WIDTH} />
        </Row>
      ) : null}
    </div>
  )
}
